import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { questionnaireRespondentSessionQuery, questionnaireRespondentSessionQueryVariables } from '../generated/questionnaireRespondentSessionQuery';
import { questionnaireRespondentSessionFieldsFragment } from '../models/QuestionnaireRespondentSession';
import { mainApiConfig } from '../../../configure/mainApiConfig';

export interface QuestionnaireRespondentSessionAsyncLoadOptions extends AsyncLoadOptions {
    questionnaireId?: string | null | undefined,
    userId?: string | null | undefined,
}
/**
 * Get a QuestionnaireRespondentSession from the store.
 * @param id
 * @param options
 */
export function useQuestionnaireRespondentSession(id: string | undefined | null, options: QuestionnaireRespondentSessionAsyncLoadOptions = {}): AsyncLoadResult<questionnaireRespondentSessionQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<questionnaireRespondentSessionQuery, questionnaireRespondentSessionQueryVariables>(
        gql`
        query questionnaireRespondentSessionQuery ($id: ID!, $userId: ID, $questionnaireId: ID) {
            model: questionnaireRespondentSession(id: $id, userId: $userId, questionnaireId: $questionnaireId ) {
                ...questionnaireRespondentSessionFields
            }
        }

        ${questionnaireRespondentSessionFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
                userId: options.userId,
                questionnaireId: options.questionnaireId,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
